import React, { useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_PHONE = '393519604932';

export default function WhatsAppFloatingButton({
  phoneNumber = DEFAULT_PHONE,
  message = 'Ciao, vorrei ricevere informazioni sui servizi di Ciesse Impianti.',
  label = 'Chatta con noi',
  helperText = 'Hai bisogno di aiuto?',
  sideKey = 'ciesse_whatsapp_float_side',
}) {
  const [side, setSide] = useState(() => {
    if (typeof window === 'undefined') return 'right';
    return localStorage.getItem(sideKey) === 'left' ? 'left' : 'right';
  });
  const [compact, setCompact] = useState(false);
  const dragStartRef = useRef(null);

  const href = useMemo(() => {
    const cleanPhone = String(phoneNumber || '').replace(/[^\d]/g, '');
    const encodedMessage = encodeURIComponent(message || '');
    return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
  }, [phoneNumber, message]);

  useEffect(() => {
    try {
      localStorage.setItem(sideKey, side);
    } catch {
      // niente: localStorage può non essere disponibile in alcuni contesti
    }
  }, [side, sideKey]);

  const startDrag = (clientX) => {
    dragStartRef.current = {
      clientX,
      sideAtStart: side,
      moved: false,
    };
  };

  const moveDrag = (clientX) => {
    if (!dragStartRef.current || typeof window === 'undefined') return;

    const delta = clientX - dragStartRef.current.clientX;
    if (Math.abs(delta) > 20) {
      dragStartRef.current.moved = true;
    }

    if (clientX < window.innerWidth / 2) {
      setSide('left');
    } else {
      setSide('right');
    }
  };

  const endDrag = () => {
    dragStartRef.current = null;
  };

  const containerPosition = side === 'left' ? 'left-4 items-start' : 'right-4 items-end';

  return (
    <div
      className={`fixed bottom-5 z-[60] flex flex-col gap-2 ${containerPosition} print:hidden`}
      onMouseDown={(e) => startDrag(e.clientX)}
      onMouseMove={(e) => moveDrag(e.clientX)}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchStart={(e) => startDrag(e.touches?.[0]?.clientX || 0)}
      onTouchMove={(e) => moveDrag(e.touches?.[0]?.clientX || 0)}
      onTouchEnd={endDrag}
    >
      {!compact && (
        <div className="max-w-[260px] rounded-2xl border border-emerald-200 bg-white/95 p-3 text-sm text-slate-700 shadow-xl backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-bold text-slate-900">{helperText}</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Apri WhatsApp e scrivi al supporto Ciesse.
              </p>
            </div>

            <button
              type="button"
              aria-label="Nascondi testo WhatsApp"
              onClick={() => setCompact(true)}
              className="shrink-0 rounded-full px-2 text-lg leading-6 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        {compact && (
          <button
            type="button"
            onClick={() => setCompact(false)}
            className="hidden rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-lg hover:bg-slate-50 sm:inline-flex"
          >
            info
          </button>
        )}

        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          aria-label={label}
          title="Trascinami a destra o sinistra se do fastidio, poi clicca per aprire WhatsApp"
          className="group flex h-16 w-16 touch-none select-none items-center justify-center rounded-full bg-[#25D366] text-white shadow-2xl shadow-emerald-900/25 ring-4 ring-white transition hover:scale-105 hover:bg-[#1ebe5d] active:scale-95"
        >
          <svg
            viewBox="0 0 32 32"
            aria-hidden="true"
            className="h-9 w-9 fill-current"
          >
            <path d="M16.01 3.2c-7.05 0-12.78 5.73-12.78 12.78 0 2.25.59 4.45 1.72 6.39L3.13 29l6.8-1.79a12.69 12.69 0 0 0 6.08 1.55h.01c7.05 0 12.78-5.73 12.78-12.78S23.06 3.2 16.01 3.2Zm0 23.39h-.01c-1.9 0-3.76-.51-5.39-1.47l-.39-.23-4.04 1.06 1.08-3.94-.25-.4a10.58 10.58 0 0 1-1.62-5.63c0-5.86 4.77-10.63 10.63-10.63 2.84 0 5.51 1.11 7.52 3.12a10.56 10.56 0 0 1 3.11 7.51c0 5.86-4.77 10.61-10.64 10.61Zm5.83-7.96c-.32-.16-1.88-.93-2.17-1.03-.29-.11-.5-.16-.71.16-.21.32-.82 1.03-1 1.24-.18.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.55.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.55-.08-.16-.71-1.72-.98-2.36-.26-.62-.52-.54-.71-.55h-.61c-.21 0-.55.08-.84.4-.29.32-1.11 1.08-1.11 2.64s1.14 3.06 1.3 3.27c.16.21 2.24 3.42 5.43 4.79.76.33 1.35.52 1.81.67.76.24 1.45.21 2 .13.61-.09 1.88-.77 2.14-1.51.26-.74.26-1.38.18-1.51-.08-.13-.29-.21-.61-.37Z" />
          </svg>
        </a>
      </div>
    </div>
  );
}
